import React, { useState, useContext, useMemo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  ListItemIcon,
  Typography,
  Tooltip,
  alpha,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useNavigate } from "react-router-dom";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LaunchIcon from "@mui/icons-material/Launch";
import PolicyIcon from "@mui/icons-material/Policy";

// Components
import CompanyList from "../components/Console-Panel/CompanyList";
import CaSettingPage from "../components/Console-Panel/CASettings";
import RecycleBin from "../components/Console-Panel/RecycleBin";
import AutoSaveConfirmationDialog from "../components/utils/dialog/autoSaveConfirmationDialog";
import AccessDenied from "../components/Common/AccessDenied";
import SubUserManagement from "../components/Console-Panel/SubUserManagement";
import DisclosureCenterPage from "../components/Console-Panel/Disclosure/DisclosureCenterPage";
import GroupReliefOptimiserPage from "./GroupReliefOptimiserPage";

// Context and Utils
import { AuthContext } from "../contexts/AuthContext";
import { handleSave } from "../utils/data/saveData";
import {
  PANEL_CA_SETTINGS,
  PANEL_COMPANY_MANAGEMENT,
  PANEL_DISCLOSURES,
  PANEL_RECYCLE_BIN,
  PANEL_USER_MANAGEMENT,
} from "../utils/data/controlData";

const DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 70;
const HEADER_HEIGHT = 64;

const ConsolePanel = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [currentComponent, setCurrentComponent] = useState("Home");
  const [selectedComponent, setSelectedComponent] = useState("Home");
  const [pendingAction, setPendingAction] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const { global_authState, checkAccess, global_dataState, resetAllData } =
    useContext(AuthContext);
  const { user_role } = global_authState;

  // Access control checks
  const canAccessConsole = checkAccess("view");
  const canViewCompanies = checkAccess("view", PANEL_COMPANY_MANAGEMENT);
  const canViewCaSettings = checkAccess("view", PANEL_CA_SETTINGS);
  const canViewUsers = checkAccess("view", PANEL_USER_MANAGEMENT);
  const canViewRecycleBin = checkAccess("view", PANEL_RECYCLE_BIN);
  const canViewDisclosures = checkAccess("view", PANEL_DISCLOSURES);

  // Move useMemo before any conditional returns
  const navItems = useMemo(
    () => [
      {
        label: "Your Companies",
        component: "Home",
        icon: <HomeIcon />,
        show: canViewCompanies,
        sectionKey: "companies",
      },
      {
        label: "Group Relief Optimiser",
        component: "Group Relief Optimiser",
        icon: <AccountTreeIcon />,
        show: true,
        sectionKey: null,
      },
      {
        label: "CA Settings",
        component: "CA Settings",
        icon: <AccountBalanceIcon />,
        show: canViewCaSettings,
        sectionKey: "caSettings",
      },
      {
        label: "Disclosures",
        component: "Disclosures",
        icon: <PolicyIcon />,
        show: canViewDisclosures,
        sectionKey: null,
      },
      {
        label: "User Management",
        component: "User Management",
        icon: <PeopleIcon />,
        show: canViewUsers && user_role === "admin",
        sectionKey: "userManagement",
      },
      {
        label: "Recycle Bin",
        component: "RecycleBin",
        icon: <DeleteIcon />,
        show: canViewRecycleBin,
        sectionKey: "recycleBin",
      },
      {
        label: "User Guide",
        component: "UserGuide",
        icon: <MenuBookIcon />,
        show: true, // Always show user guide
        sectionKey: null,
        externalLink: "https://tax-savy.odoo.com/knowledge/article/37",
      },
    ],
    [
      canViewCompanies,
      canViewCaSettings,
      canViewUsers,
      canViewRecycleBin,
      canViewDisclosures,
      user_role,
    ]
  );

  // If no access to console, show access denied
  if (!canAccessConsole) {
    return (
      <AccessDenied
        title='Access Denied'
        message="You don't have permission to access the admin console. Please contact your administrator if you believe this is an error."
        customAction={
          <Button
            variant='contained'
            color='primary'
            onClick={() => navigate("/")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
          >
            Return to Home
          </Button>
        }
      />
    );
  }

  const renderComponent = () => {
    switch (selectedComponent) {
      case "Home":
        return canViewCompanies ? (
          <CompanyList />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to view companies."
          />
        );
      case "CA Settings":
        return canViewCaSettings ? (
          <CaSettingPage />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to view CA settings."
          />
        );
      case "User Management":
        return canViewUsers && user_role === "admin" ? (
          <SubUserManagement />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to manage users."
          />
        );
      case "RecycleBin":
        return canViewRecycleBin ? (
          <RecycleBin />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to view the recycle bin."
          />
        );
      case "Disclosures":
        return canViewDisclosures ? (
          <DisclosureCenterPage />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to view disclosures."
          />
        );
      case "Group Relief Optimiser":
        return <GroupReliefOptimiserPage />;
      default:
        return canViewCompanies ? (
          <CompanyList />
        ) : (
          <AccessDenied
            title='Access Denied'
            message="You don't have permission to view companies."
          />
        );
    }
  };

  const getSectionKeyByLabel = (label) => {
    const item = navItems.find((nav) => nav.label === label);
    return item ? item.sectionKey : null;
  };

  const checkUnsavedChanges = (sectionKey, action) => {
    if (global_dataState[sectionKey]?.unsaved) {
      setPendingAction(() => action);
      setSaveDialogOpen(true);
    } else {
      action();
    }
  };

  const handleSaveAndProceed = async () => {
    const data = global_dataState[getSectionKeyByLabel(currentComponent)];
    try {
      await handleSave(
        getSectionKeyByLabel(currentComponent),
        [data],
        global_authState
      );
      setSaveDialogOpen(false);
      if (pendingAction) {
        await resetAllData();
        pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleConfirmLeave = async () => {
    try {
      setSaveDialogOpen(false);
      if (pendingAction) {
        await resetAllData();
        pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      console.error("Error handling leave:", error);
    }
  };

  const handleNavClick = (component) => {
    const item = navItems.find((nav) => nav.component === component);
    if (item?.externalLink) {
      window.open(item.externalLink, "_blank");
      return;
    }

    checkUnsavedChanges(getSectionKeyByLabel(currentComponent), async () => {
      setSelectedComponent(component);
      setCurrentComponent(component);
    });
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <Box
        sx={{
          display: "flex",
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          overflow: "hidden",
          bgcolor: "background.default",
          position: "relative",
        }}
      >
        <Drawer
          variant='permanent'
          sx={{
            width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
            flexShrink: 0,
            height: "100%",
            position: "relative",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            "& .MuiDrawer-paper": {
              position: "relative",
              width: isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
              height: "100%",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              boxSizing: "border-box",
              borderRight: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isDrawerOpen ? "space-between" : "center",
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              height: 64,
            }}
          >
            {isDrawerOpen && (
              <Typography
                variant='h6'
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                {user_role === "admin" ? "Admin Console" : "User Console"}
              </Typography>
            )}
            <IconButton onClick={toggleDrawer}>
              {isDrawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

          <Box
            sx={{
              overflow: "auto",
              height: `calc(100% - 64px)`,
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.3),
                },
              },
            }}
          >
            <List sx={{ p: 1 }}>
              {navItems
                .filter((item) => item.show)
                .map((item, index) => (
                  <Tooltip
                    key={index}
                    title={!isDrawerOpen ? item.label : ""}
                    placement='right'
                  >
                    <ListItem
                      button
                      onClick={() => handleNavClick(item.component)}
                      sx={{
                        mb: 0.5,
                        borderRadius: 1,
                        bgcolor:
                          selectedComponent === item.component
                            ? (theme) => alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                        color:
                          selectedComponent === item.component
                            ? "primary.main"
                            : "text.primary",
                        "&:hover": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.12),
                          transform: "translateX(4px)",
                        },
                        transition: "transform 0.2s ease-in-out",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color:
                            selectedComponent === item.component
                              ? "primary.main"
                              : "text.secondary",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {isDrawerOpen && (
                        <>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight:
                                selectedComponent === item.component
                                  ? 600
                                  : 400,
                            }}
                          />
                          {item.externalLink && (
                            <LaunchIcon
                              fontSize='small'
                              sx={{ ml: 1, opacity: 0.7 }}
                            />
                          )}
                        </>
                      )}
                    </ListItem>
                  </Tooltip>
                ))}
            </List>
          </Box>
        </Drawer>

        <Box
          component='main'
          sx={{
            flexGrow: 1,
            width: `calc(100% - ${
              isDrawerOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH
            }px)`,
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflow: "auto",
          }}
        >
          {renderComponent()}
        </Box>

        <AutoSaveConfirmationDialog
          open={saveDialogOpen}
          data={global_dataState[getSectionKeyByLabel(currentComponent)]}
          onCancel={() => setSaveDialogOpen(false)}
          onLeave={handleConfirmLeave}
          onConfirm={handleSaveAndProceed}
          section={currentComponent}
        />
    </Box>
  );
};

export default ConsolePanel;
